import i18n, { type Resource } from 'i18next'
import { initReactI18next } from 'react-i18next'
import { MARKET, MARKET_CODE } from '@/market'
import { mergeLocale } from './merge'
import ru from './locales/ru'
import uz from './locales/uz'
import en from './locales/en'
import geKa from './locales/ge/ka'
import geRu from './locales/ge/ru'
import geEn from './locales/ge/en'

/** Each market ships its own languages; samly.ge overlays its copy on the shared base texts. */
const resources: Resource =
  MARKET_CODE === 'ge'
    ? {
        ka: { translation: geKa },
        ru: { translation: mergeLocale(ru, geRu) },
        en: { translation: mergeLocale(en, geEn) },
      }
    : {
        ru: { translation: ru },
        uz: { translation: uz },
        en: { translation: en },
      }

export const SUPPORTED_LANGUAGES = MARKET.languages.map(l => l.code)

const STORAGE_KEY = 'samly.lang'

function isSupported(value: string | null | undefined): value is string {
  return !!value && SUPPORTED_LANGUAGES.includes(value)
}

/** Remembered choice first, then the browser's preference, then the market default. */
function detectLanguage(): string {
  try {
    const stored = localStorage.getItem(STORAGE_KEY)
    if (isSupported(stored)) return stored
  } catch {
    // Private mode or blocked storage — fall through to the browser preference.
  }

  const preferred = navigator.languages?.length ? navigator.languages : [navigator.language]
  for (const candidate of preferred) {
    const base = candidate?.toLowerCase().split('-')[0]
    if (isSupported(base)) return base
  }

  return MARKET.defaultLanguage
}

i18n.use(initReactI18next).init({
  resources,
  lng: detectLanguage(),
  fallbackLng: MARKET.fallbackLanguage,
  interpolation: {
    escapeValue: false,
  },
})

/** Keep <html lang> and the remembered choice in step with i18next. */
function syncLanguage(lng: string) {
  document.documentElement.lang = lng
  try {
    localStorage.setItem(STORAGE_KEY, lng)
  } catch {
    // The choice just won't survive a reload — not worth surfacing.
  }
}

syncLanguage(i18n.language)
i18n.on('languageChanged', syncLanguage)

export default i18n
